/**
 * Deliveries / Dispatch tracking page.
 *
 * Page composition:
 *   - PageHeader + "Create Delivery" action
 *   - KPI strip (Pending, In Transit, Delivered Today, Success Rate)
 *   - Filter bar (status, date range, search)
 *   - DataTable
 *   - Detail Sheet with delivery timeline
 *   - Create Delivery Sheet
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Plus,
  Truck,
  Check,
  Eye,
  Search,
  Filter,
  Clock,
  AlertTriangle,
  CheckCircle,
  Package,
  PackageCheck,
  MapPin,
  Calendar,
  ArrowRight,
  Edit,
  Printer,
} from "lucide-react";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  cn,
  DataTable,
  Input,
  KpiCard,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  type StatusPillVariant,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";

/* ─────────────────────────────────────────────────────────────
 * Types
 * ───────────────────────────────────────────────────────────── */

type DeliveryStatus =
  | "pending"
  | "dispatched"
  | "in_transit"
  | "delivered"
  | "failed";

type MockDelivery = {
  _id: string;
  deliveryNumber: string;
  invoiceId: string;
  invoiceNumber: string;
  dealerId: string;
  dealerName: string;
  status: DeliveryStatus;
  items: Array<{
    productName: string;
    qty: number;
    qtyDelivered: number;
  }>;
  totalItems: number;
  dispatchedAt: number | null;
  deliveredAt: number | null;
  expectedDeliveryAt: number;
  driverName: string;
  driverPhone: string;
  vehicleNumber: string;
  deliveryAddress: string;
  notes: string;
  proofOfDelivery: boolean;
  createdAt: number;
  updatedAt: number;
};

/* ─────────────────────────────────────────────────────────────
 * Constants
 * ───────────────────────────────────────────────────────────── */

const NOW = new Date("2026-05-09T08:00:00Z").getTime();
const DAY = 24 * 60 * 60 * 1000;
const HOUR = 60 * 60 * 1000;

const STATUS_VARIANTS: Record<DeliveryStatus, StatusPillVariant> = {
  pending: "neutral",
  dispatched: "info",
  in_transit: "warning",
  delivered: "success",
  failed: "error",
};

const STATUS_LABELS: Record<DeliveryStatus, string> = {
  pending: "Pending",
  dispatched: "Dispatched",
  in_transit: "In Transit",
  delivered: "Delivered",
  failed: "Failed",
};

const ALL_STATUSES: DeliveryStatus[] = [
  "pending",
  "dispatched",
  "in_transit",
  "delivered",
  "failed",
];

/* ─────────────────────────────────────────────────────────────
 * Mock data — 10 sample rows
 * ───────────────────────────────────────────────────────────── */

const mockDeliveries = ([
  {
    _id: "del_001",
    deliveryNumber: "DEL-0001",
    invoiceId: "inv_001",
    invoiceNumber: "INV-2026-0001",
    dealerId: "dealer_01",
    dealerName: "Dhaka Wholesale Traders",
    status: "delivered",
    items: [
      { productName: "Basmati Rice 5kg", qty: 50, qtyDelivered: 50 },
      { productName: "Sunflower Oil 1L", qty: 30, qtyDelivered: 30 },
    ],
    totalItems: 80,
    dispatchedAt: NOW - 5 * DAY,
    deliveredAt: NOW - 4 * DAY,
    expectedDeliveryAt: NOW - 4 * DAY,
    driverName: "Kamal Hossain",
    driverPhone: "+880-1711-234567",
    vehicleNumber: "DHA-KA-21-4532",
    deliveryAddress: "45 Motijheel C/A, Dhaka-1000",
    notes: "Delivered to warehouse gate. Signed by store manager.",
    proofOfDelivery: true,
    createdAt: NOW - 6 * DAY,
  },
  {
    _id: "del_002",
    deliveryNumber: "DEL-0002",
    invoiceId: "inv_002",
    invoiceNumber: "INV-2026-0002",
    dealerId: "dealer_02",
    dealerName: "Chittagong Supply Co.",
    status: "in_transit",
    items: [
      { productName: "Atta Flour 10kg", qty: 100, qtyDelivered: 0 },
      { productName: "Sugar 5kg", qty: 60, qtyDelivered: 0 },
      { productName: "Salt 1kg", qty: 200, qtyDelivered: 0 },
    ],
    totalItems: 360,
    dispatchedAt: NOW - 1 * DAY,
    deliveredAt: null,
    expectedDeliveryAt: NOW + 1 * DAY,
    driverName: "Rafiq Ahmed",
    driverPhone: "+880-1812-345678",
    vehicleNumber: "CTG-GA-19-7890",
    deliveryAddress: "12 Agrabad C/A, Chittagong-4100",
    notes: "Heavy load. Use rear loading dock.",
    proofOfDelivery: false,
    createdAt: NOW - 2 * DAY,
  },
  {
    _id: "del_003",
    deliveryNumber: "DEL-0003",
    invoiceId: "inv_003",
    invoiceNumber: "INV-2026-0003",
    dealerId: "dealer_03",
    dealerName: "Sylhet Grocers Ltd.",
    status: "pending",
    items: [
      { productName: "Mustard Oil 500ml", qty: 80, qtyDelivered: 0 },
      { productName: "Turmeric Powder 200g", qty: 150, qtyDelivered: 0 },
    ],
    totalItems: 230,
    dispatchedAt: null,
    deliveredAt: null,
    expectedDeliveryAt: NOW + 2 * DAY,
    driverName: "Jamal Uddin",
    driverPhone: "+880-1911-456789",
    vehicleNumber: "SYL-HA-22-1234",
    deliveryAddress: "78 Zindabazar, Sylhet-3100",
    notes: "Call dealer 30 mins before arrival.",
    proofOfDelivery: false,
    createdAt: NOW - 1 * DAY,
  },
  {
    _id: "del_004",
    deliveryNumber: "DEL-0004",
    invoiceId: "inv_004",
    invoiceNumber: "INV-2026-0004",
    dealerId: "dealer_04",
    dealerName: "Rajshahi Agro Mart",
    status: "delivered",
    items: [
      { productName: "Basmati Rice 5kg", qty: 40, qtyDelivered: 40 },
    ],
    totalItems: 40,
    dispatchedAt: NOW - 3 * DAY,
    deliveredAt: NOW - 2 * DAY,
    expectedDeliveryAt: NOW - 2 * DAY,
    driverName: "Sohel Rana",
    driverPhone: "+880-1611-567890",
    vehicleNumber: "RAJ-DA-20-5678",
    deliveryAddress: "23 Shaheb Bazar, Rajshahi-6100",
    notes: "",
    proofOfDelivery: true,
    createdAt: NOW - 4 * DAY,
  },
  {
    _id: "del_005",
    deliveryNumber: "DEL-0005",
    invoiceId: "inv_005",
    invoiceNumber: "INV-2026-0005",
    dealerId: "dealer_05",
    dealerName: "Khulna Distribution Hub",
    status: "failed",
    items: [
      { productName: "Sunflower Oil 1L", qty: 60, qtyDelivered: 0 },
      { productName: "Atta Flour 10kg", qty: 25, qtyDelivered: 0 },
    ],
    totalItems: 85,
    dispatchedAt: NOW - 4 * DAY,
    deliveredAt: null,
    expectedDeliveryAt: NOW - 3 * DAY,
    driverName: "Moni Mia",
    driverPhone: "+880-1511-678901",
    vehicleNumber: "KHL-KA-18-9012",
    deliveryAddress: "56 KDA Avenue, Khulna-9000",
    notes: "Dealer shop was closed. Attempted delivery twice.",
    proofOfDelivery: false,
    createdAt: NOW - 5 * DAY,
  },
  {
    _id: "del_006",
    deliveryNumber: "DEL-0006",
    invoiceId: "inv_006",
    invoiceNumber: "INV-2026-0006",
    dealerId: "dealer_06",
    dealerName: "Barisal Fresh Market",
    status: "dispatched",
    items: [
      { productName: "Sugar 5kg", qty: 45, qtyDelivered: 0 },
      { productName: "Salt 1kg", qty: 100, qtyDelivered: 0 },
      { productName: "Turmeric Powder 200g", qty: 75, qtyDelivered: 0 },
    ],
    totalItems: 220,
    dispatchedAt: NOW - 2 * HOUR,
    deliveredAt: null,
    expectedDeliveryAt: NOW + 6 * HOUR,
    driverName: "Habib Rahman",
    driverPhone: "+880-1411-789012",
    vehicleNumber: "BAR-GA-21-3456",
    deliveryAddress: "34 Sadar Road, Barisal-8200",
    notes: "Fragile items on top. Handle with care.",
    proofOfDelivery: false,
    createdAt: NOW - 1 * DAY,
  },
  {
    _id: "del_007",
    deliveryNumber: "DEL-0007",
    invoiceId: "inv_007",
    invoiceNumber: "INV-2026-0007",
    dealerId: "dealer_01",
    dealerName: "Dhaka Wholesale Traders",
    status: "pending",
    items: [
      { productName: "Mustard Oil 500ml", qty: 120, qtyDelivered: 0 },
    ],
    totalItems: 120,
    dispatchedAt: null,
    deliveredAt: null,
    expectedDeliveryAt: NOW + 3 * DAY,
    driverName: "Kamal Hossain",
    driverPhone: "+880-1711-234567",
    vehicleNumber: "DHA-KA-21-4532",
    deliveryAddress: "45 Motijheel C/A, Dhaka-1000",
    notes: "Second delivery this week for same dealer.",
    proofOfDelivery: false,
    createdAt: NOW,
  },
  {
    _id: "del_008",
    deliveryNumber: "DEL-0008",
    invoiceId: "inv_008",
    invoiceNumber: "INV-2026-0008",
    dealerId: "dealer_03",
    dealerName: "Sylhet Grocers Ltd.",
    status: "delivered",
    items: [
      { productName: "Basmati Rice 5kg", qty: 30, qtyDelivered: 30 },
      { productName: "Sugar 5kg", qty: 20, qtyDelivered: 18 },
    ],
    totalItems: 50,
    dispatchedAt: NOW - 7 * DAY,
    deliveredAt: NOW - 6 * DAY,
    expectedDeliveryAt: NOW - 6 * DAY,
    driverName: "Jamal Uddin",
    driverPhone: "+880-1911-456789",
    vehicleNumber: "SYL-HA-22-1234",
    deliveryAddress: "78 Zindabazar, Sylhet-3100",
    notes: "2 units of Sugar 5kg damaged in transit. Partial delivery.",
    proofOfDelivery: true,
    createdAt: NOW - 8 * DAY,
  },
  {
    _id: "del_009",
    deliveryNumber: "DEL-0009",
    invoiceId: "inv_009",
    invoiceNumber: "INV-2026-0009",
    dealerId: "dealer_02",
    dealerName: "Chittagong Supply Co.",
    status: "in_transit",
    items: [
      { productName: "Atta Flour 10kg", qty: 70, qtyDelivered: 0 },
      { productName: "Sunflower Oil 1L", qty: 40, qtyDelivered: 0 },
    ],
    totalItems: 110,
    dispatchedAt: NOW - 8 * HOUR,
    deliveredAt: null,
    expectedDeliveryAt: NOW + 4 * HOUR,
    driverName: "Rafiq Ahmed",
    driverPhone: "+880-1812-345678",
    vehicleNumber: "CTG-GA-19-7890",
    deliveryAddress: "12 Agrabad C/A, Chittagong-4100",
    notes: "",
    proofOfDelivery: false,
    createdAt: NOW - 1 * DAY,
  },
  {
    _id: "del_010",
    deliveryNumber: "DEL-0010",
    invoiceId: "inv_010",
    invoiceNumber: "INV-2026-0010",
    dealerId: "dealer_04",
    dealerName: "Rajshahi Agro Mart",
    status: "pending",
    items: [
      { productName: "Turmeric Powder 200g", qty: 200, qtyDelivered: 0 },
      { productName: "Salt 1kg", qty: 150, qtyDelivered: 0 },
      { productName: "Mustard Oil 500ml", qty: 50, qtyDelivered: 0 },
    ],
    totalItems: 400,
    dispatchedAt: null,
    deliveredAt: null,
    expectedDeliveryAt: NOW + 4 * DAY,
    driverName: "Sohel Rana",
    driverPhone: "+880-1611-567890",
    vehicleNumber: "RAJ-DA-20-5678",
    deliveryAddress: "23 Shaheb Bazar, Rajshahi-6100",
    notes: "Large order. May require two trips.",
    proofOfDelivery: false,
    createdAt: NOW,
  },
] as Omit<MockDelivery, "updatedAt">[]).map((d) => ({ ...d, updatedAt: d.createdAt })) as MockDelivery[];

/* ─────────────────────────────────────────────────────────────
 * Route
 * ───────────────────────────────────────────────────────────── */

export const Route = createFileRoute("/_app/sales/deliveries")({
  component: DeliveriesPage,
});

/* ─────────────────────────────────────────────────────────────
 * Helpers
 * ───────────────────────────────────────────────────────────── */

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/* ─────────────────────────────────────────────────────────────
 * Main page
 * ───────────────────────────────────────────────────────────── */

function DeliveriesPage() {
  const isMock = useMockData();
  const [rows, setRows] = usePersistentMockState<MockDelivery[]>(
    "deliveries",
    mockDeliveries,
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockDelivery | null>(null);

  /* Filters */
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  /* Filtered rows */
  const filteredRows = useMemo(() => {
    let result = rows;

    if (statusFilter !== "all") {
      result = result.filter((r) => r.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (r) =>
          r.deliveryNumber.toLowerCase().includes(q) ||
          r.dealerName.toLowerCase().includes(q) ||
          r.invoiceNumber.toLowerCase().includes(q) ||
          r.driverName.toLowerCase().includes(q),
      );
    }

    if (dateFrom) {
      const from = new Date(dateFrom).getTime();
      result = result.filter((r) => r.createdAt >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo).getTime() + DAY;
      result = result.filter((r) => r.createdAt < to);
    }

    return result;
  }, [rows, statusFilter, searchQuery, dateFrom, dateTo]);

  /* KPIs */
  const kpis = useMemo(() => {
    const todayStart = startOfDay(NOW);
    const todayEnd = todayStart + DAY;
    let pending = 0;
    let inTransit = 0;
    let deliveredToday = 0;
    let totalCompleted = 0;
    let totalFailed = 0;

    for (const r of rows) {
      if (r.status === "pending") pending++;
      if (r.status === "in_transit" || r.status === "dispatched") inTransit++;
      if (r.status === "delivered") {
        totalCompleted++;
        if (
          r.deliveredAt !== null &&
          r.deliveredAt >= todayStart &&
          r.deliveredAt < todayEnd
        ) {
          deliveredToday++;
        }
      }
      if (r.status === "failed") totalFailed++;
    }

    const totalFinished = totalCompleted + totalFailed;
    const successRate =
      totalFinished > 0
        ? Math.round((totalCompleted / totalFinished) * 100)
        : 100;

    return { pending, inTransit, deliveredToday, successRate };
  }, [rows]);

  /* Table columns */
  const columns = useMemo(
    () => [
      {
        accessorKey: "deliveryNumber",
        header: "Delivery #",
        cell: ({ row }: { row: { original: MockDelivery } }) => (
          <span className="font-mono text-[12px]">
            {row.original.deliveryNumber}
          </span>
        ),
        meta: { mono: true },
      },
      {
        accessorKey: "invoiceNumber",
        header: "Invoice #",
        cell: ({ row }: { row: { original: MockDelivery } }) => (
          <span className="font-mono text-[12px] text-(--text-secondary)">
            {row.original.invoiceNumber}
          </span>
        ),
        meta: { mono: true, hideOnMobile: true },
      },
      {
        id: "dealer",
        header: "Dealer",
        cell: ({ row }: { row: { original: MockDelivery } }) => (
          <span className="text-(--text-primary)">
            {row.original.dealerName}
          </span>
        ),
      },
      {
        id: "items",
        header: "Items",
        cell: ({ row }: { row: { original: MockDelivery } }) => (
          <span className="tabular-nums">
            {row.original.totalItems} units
          </span>
        ),
        meta: { numeric: true, hideOnMobile: true },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }: { row: { original: MockDelivery } }) => (
          <StatusPill variant={STATUS_VARIANTS[row.original.status]}>
            {STATUS_LABELS[row.original.status]}
          </StatusPill>
        ),
      },
      {
        id: "expectedDate",
        header: "Expected",
        cell: ({ row }: { row: { original: MockDelivery } }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatDate(row.original.expectedDeliveryAt)}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "driver",
        header: "Driver",
        cell: ({ row }: { row: { original: MockDelivery } }) => (
          <span className="text-(--text-secondary)">
            {row.original.driverName}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }: { row: { original: MockDelivery } }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setDetailRow(row.original);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
        ),
        meta: { shrink: true },
      },
    ],
    [],
  );

  /* Actions */
  function handleMarkDispatched(delivery: MockDelivery) {
    if (!isMock) return;
    setRows((prev) =>
      prev.map((r) =>
        r._id === delivery._id
          ? { ...r, status: "dispatched" as const, dispatchedAt: Date.now() }
          : r,
      ),
    );
    setDetailRow((prev) =>
      prev?._id === delivery._id
        ? { ...prev, status: "dispatched" as const, dispatchedAt: Date.now() }
        : prev,
    );
    toast.success(`${delivery.deliveryNumber} marked as dispatched (mock)`);
  }

  function handleMarkInTransit(delivery: MockDelivery) {
    if (!isMock) return;
    setRows((prev) =>
      prev.map((r) =>
        r._id === delivery._id
          ? { ...r, status: "in_transit" as const }
          : r,
      ),
    );
    setDetailRow((prev) =>
      prev?._id === delivery._id
        ? { ...prev, status: "in_transit" as const }
        : prev,
    );
    toast.success(`${delivery.deliveryNumber} marked as in transit (mock)`);
  }

  function handleMarkDelivered(delivery: MockDelivery) {
    if (!isMock) return;
    const now = Date.now();
    setRows((prev) =>
      prev.map((r) =>
        r._id === delivery._id
          ? {
              ...r,
              status: "delivered" as const,
              deliveredAt: now,
              proofOfDelivery: true,
              items: r.items.map((item) => ({
                ...item,
                qtyDelivered: item.qty,
              })),
            }
          : r,
      ),
    );
    setDetailRow((prev) =>
      prev?._id === delivery._id
        ? {
            ...prev,
            status: "delivered" as const,
            deliveredAt: now,
            proofOfDelivery: true,
            items: prev.items.map((item) => ({
              ...item,
              qtyDelivered: item.qty,
            })),
          }
        : prev,
    );
    toast.success(`${delivery.deliveryNumber} marked as delivered (mock)`);
  }

  function handleMarkFailed(delivery: MockDelivery) {
    if (!isMock) return;
    setRows((prev) =>
      prev.map((r) =>
        r._id === delivery._id
          ? { ...r, status: "failed" as const }
          : r,
      ),
    );
    setDetailRow((prev) =>
      prev?._id === delivery._id
        ? { ...prev, status: "failed" as const }
        : prev,
    );
    toast.success(`${delivery.deliveryNumber} marked as failed (mock)`);
  }

  function handleCreate(values: CreateDeliveryValues) {
    if (!isMock) return;
    const maxNum = rows.reduce((max, r) => {
      const n = parseInt(r.deliveryNumber.replace("DEL-", ""), 10);
      return n > max ? n : max;
    }, 0);
    const newNumber = `DEL-${String(maxNum + 1).padStart(4, "0")}`;
    const newRow: MockDelivery = {
      _id: crypto.randomUUID(),
      deliveryNumber: newNumber,
      invoiceId: values.invoiceId,
      invoiceNumber: values.invoiceNumber,
      dealerId: values.dealerId,
      dealerName: values.dealerName,
      status: "pending",
      items: values.items.map((item) => ({
        productName: item.productName,
        qty: item.qty,
        qtyDelivered: 0,
      })),
      totalItems: values.items.reduce((sum, i) => sum + i.qty, 0),
      dispatchedAt: null,
      deliveredAt: null,
      expectedDeliveryAt: new Date(values.expectedDeliveryAt).getTime(),
      driverName: values.driverName,
      driverPhone: values.driverPhone,
      vehicleNumber: values.vehicleNumber,
      deliveryAddress: values.deliveryAddress,
      notes: values.notes,
      proofOfDelivery: false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    setRows((prev) => [newRow, ...prev]);
    setCreateOpen(false);
    toast.success(`Delivery ${newNumber} created (mock)`);
  }

  /* CSV export */
  function handleExportCsv(extension: "csv" | "xls") {
    const cols: ExportColumn<MockDelivery>[] = [
      { key: "deliveryNumber", label: "Delivery #" },
      { key: "invoiceNumber", label: "Invoice #" },
      { key: "dealerName", label: "Dealer" },
      { key: "totalItems", label: "Total Items" },
      {
        key: "status",
        label: "Status",
        format: (v: unknown) =>
          STATUS_LABELS[v as DeliveryStatus] ?? String(v),
      },
      {
        key: "expectedDeliveryAt",
        label: "Expected Delivery",
        format: (v: unknown) => formatDateForCsv(Number(v)),
      },
      {
        key: "dispatchedAt",
        label: "Dispatched At",
        format: (v: unknown) =>
          formatDateForCsv(v as number | null | undefined),
      },
      {
        key: "deliveredAt",
        label: "Delivered At",
        format: (v: unknown) =>
          formatDateForCsv(v as number | null | undefined),
      },
      { key: "driverName", label: "Driver" },
      { key: "driverPhone", label: "Driver Phone" },
      { key: "vehicleNumber", label: "Vehicle" },
      { key: "deliveryAddress", label: "Address" },
      {
        key: "createdAt",
        label: "Created",
        format: (v: unknown) => formatDateForCsv(Number(v)),
      },
    ];
    downloadCsv(
      exportFilename("deliveries", extension),
      toCsv(filteredRows, cols),
    );
  }

  function clearFilters() {
    setStatusFilter("all");
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
  }

  const hasActiveFilters =
    statusFilter !== "all" ||
    searchQuery.trim() !== "" ||
    dateFrom !== "" ||
    dateTo !== "";

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Deliveries"
        description="Track dispatches and deliveries to dealers."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Create Delivery
            </Button>
          </>
        }
      />

      {/* KPI strip */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Delivery summary"
      >
        <KpiCard
          caption="Pending Dispatch"
          value={String(kpis.pending)}
          hint="Awaiting dispatch"
        />
        <KpiCard
          caption="In Transit"
          value={String(kpis.inTransit)}
          hint="Dispatched or en route"
        />
        <KpiCard
          caption="Delivered Today"
          value={String(kpis.deliveredToday)}
          hint="Completed today"
        />
        <KpiCard
          caption="Success Rate"
          value={`${kpis.successRate}%`}
          hint="Delivered / (Delivered + Failed)"
        />
      </section>

      {/* Filter bar */}
      <section className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
            Status
          </Label>
          <Select
            value={statusFilter}
            onValueChange={(v: string) => setStatusFilter(v)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {ALL_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
            From
          </Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDateFrom(e.target.value)
            }
            className="w-[150px]"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
            To
          </Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDateTo(e.target.value)
            }
            className="w-[150px]"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
            Search
          </Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-(--text-tertiary)" />
            <Input
              placeholder="Delivery #, dealer, driver..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
              className="pl-9 w-[240px]"
            />
          </div>
        </div>

        {hasActiveFilters ? (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : null}
      </section>

      {/* Data table */}
      <DataTable
        data={filteredRows}
        columns={columns}
        getRowId={(r: MockDelivery) => r._id}
        onRowClick={(row: { original: MockDelivery }) =>
          setDetailRow(row.original)
        }
        emptyTitle="No deliveries"
        emptyDescription="Create a delivery to start tracking dispatches."
        emptyIcon={<Truck className="h-5 w-5" aria-hidden />}
        emptyAction={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Create Delivery
          </Button>
        }
      />

      {/* Detail sheet */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open: boolean) => {
          if (!open) setDetailRow(null);
        }}
      >
        <SheetContent side="right" size="md">
          {detailRow ? (
            <DeliveryDetail
              row={detailRow}
              onMarkDispatched={() => handleMarkDispatched(detailRow)}
              onMarkInTransit={() => handleMarkInTransit(detailRow)}
              onMarkDelivered={() => handleMarkDelivered(detailRow)}
              onMarkFailed={() => handleMarkFailed(detailRow)}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Create sheet */}
      <Sheet open={createOpen} onOpenChange={(open: boolean) => setCreateOpen(open)}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Create Delivery</SheetTitle>
            <SheetDescription>
              Enter delivery details. Select an invoice to auto-fill dealer and
              item information.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <CreateDeliveryForm
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail sheet
 * ───────────────────────────────────────────────────────────── */

function DeliveryDetail({
  row,
  onMarkDispatched,
  onMarkInTransit,
  onMarkDelivered,
  onMarkFailed,
}: {
  row: MockDelivery;
  onMarkDispatched: () => void;
  onMarkInTransit: () => void;
  onMarkDelivered: () => void;
  onMarkFailed: () => void;
}) {
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <span className="font-mono">{row.deliveryNumber}</span>
          <StatusPill variant={STATUS_VARIANTS[row.status]}>
            {STATUS_LABELS[row.status]}
          </StatusPill>
        </SheetTitle>
        <SheetDescription>{row.dealerName}</SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-6">
          {/* Timeline */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-3">
              Delivery Timeline
            </div>
            <DeliveryTimeline delivery={row} />
          </section>

          {/* Core details */}
          <section className="grid grid-cols-2 gap-3">
            <DetailField
              label="Invoice"
              value={
                <span className="font-mono">{row.invoiceNumber}</span>
              }
            />
            <DetailField label="Dealer" value={row.dealerName} />
            <DetailField
              label="Expected Delivery"
              value={formatDate(row.expectedDeliveryAt)}
            />
            <DetailField
              label="Created"
              value={formatDateTime(row.createdAt)}
            />
            {row.dispatchedAt ? (
              <DetailField
                label="Dispatched"
                value={formatDateTime(row.dispatchedAt)}
              />
            ) : null}
            {row.deliveredAt ? (
              <DetailField
                label="Delivered"
                value={formatDateTime(row.deliveredAt)}
              />
            ) : null}
          </section>

          {/* Driver / Vehicle */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Driver &amp; Vehicle
            </div>
            <div className="grid grid-cols-2 gap-3">
              <DetailField label="Driver" value={row.driverName} />
              <DetailField label="Phone" value={row.driverPhone} />
              <DetailField label="Vehicle" value={row.vehicleNumber} />
              <DetailField
                label="Proof of Delivery"
                value={row.proofOfDelivery ? "Yes" : "No"}
              />
            </div>
          </section>

          {/* Delivery address */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Delivery Address
            </div>
            <div className="flex items-start gap-2 text-[13px] text-(--text-secondary)">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-(--text-tertiary)" />
              <span>{row.deliveryAddress}</span>
            </div>
          </section>

          {/* Items */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Items ({row.totalItems} units)
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b bg-(--bg-secondary)">
                    <th className="text-left px-3 py-2 font-medium text-(--text-secondary)">
                      Product
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-(--text-secondary)">
                      Ordered
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-(--text-secondary)">
                      Delivered
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {row.items.map((item, idx) => (
                    <tr
                      key={idx}
                      className={cn(
                        "border-b last:border-b-0",
                        item.qtyDelivered < item.qty &&
                          row.status === "delivered"
                          ? "bg-amber-50 dark:bg-amber-950/20"
                          : "",
                      )}
                    >
                      <td className="px-3 py-2 text-(--text-primary)">
                        {item.productName}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {item.qty}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {item.qtyDelivered}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Notes */}
          {row.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {row.notes}
              </p>
            </section>
          ) : null}
        </div>
      </SheetBody>
      <SheetFooter>
        {row.status === "pending" ? (
          <>
            <Button onClick={onMarkDispatched}>
              <Truck className="h-4 w-4" /> Mark Dispatched
            </Button>
            <Button variant="destructive" onClick={onMarkFailed}>
              <AlertTriangle className="h-4 w-4" /> Mark Failed
            </Button>
          </>
        ) : null}
        {row.status === "dispatched" ? (
          <>
            <Button onClick={onMarkInTransit}>
              <ArrowRight className="h-4 w-4" /> Mark In Transit
            </Button>
            <Button variant="destructive" onClick={onMarkFailed}>
              <AlertTriangle className="h-4 w-4" /> Mark Failed
            </Button>
          </>
        ) : null}
        {row.status === "in_transit" ? (
          <>
            <Button onClick={onMarkDelivered}>
              <CheckCircle className="h-4 w-4" /> Mark Delivered
            </Button>
            <Button variant="destructive" onClick={onMarkFailed}>
              <AlertTriangle className="h-4 w-4" /> Mark Failed
            </Button>
          </>
        ) : null}
        {row.status === "failed" ? (
          <Button onClick={onMarkDispatched}>
            <Truck className="h-4 w-4" /> Retry Dispatch
          </Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Delivery timeline visualization
 * ───────────────────────────────────────────────────────────── */

const TIMELINE_STEPS: Array<{
  key: DeliveryStatus;
  label: string;
  icon: typeof Package;
}> = [
  { key: "pending", label: "Created", icon: Package },
  { key: "dispatched", label: "Dispatched", icon: Truck },
  { key: "in_transit", label: "In Transit", icon: ArrowRight },
  { key: "delivered", label: "Delivered", icon: CheckCircle },
];

const STATUS_ORDER: Record<DeliveryStatus, number> = {
  pending: 0,
  dispatched: 1,
  in_transit: 2,
  delivered: 3,
  failed: -1,
};

function DeliveryTimeline({ delivery }: { delivery: MockDelivery }) {
  const currentStep = STATUS_ORDER[delivery.status];
  const isFailed = delivery.status === "failed";

  function getStepTimestamp(stepKey: DeliveryStatus): number | null {
    switch (stepKey) {
      case "pending":
        return delivery.createdAt;
      case "dispatched":
        return delivery.dispatchedAt;
      case "in_transit":
        return delivery.dispatchedAt; // approximate
      case "delivered":
        return delivery.deliveredAt;
      default:
        return null;
    }
  }

  return (
    <div className="flex flex-col gap-0">
      {TIMELINE_STEPS.map((step, idx) => {
        const stepOrder = STATUS_ORDER[step.key];
        const isCompleted = !isFailed && currentStep >= stepOrder;
        const isCurrent = !isFailed && currentStep === stepOrder;
        const ts = getStepTimestamp(step.key);
        const Icon = step.icon;
        const isLast = idx === TIMELINE_STEPS.length - 1;

        return (
          <div key={step.key} className="flex items-start gap-3">
            {/* Vertical line + dot */}
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center shrink-0 border-2",
                  isCompleted
                    ? "border-green-500 bg-green-50 text-green-600 dark:bg-green-950/30 dark:text-green-400"
                    : isFailed && stepOrder > 0
                      ? "border-red-300 bg-red-50 text-red-400 dark:bg-red-950/30 dark:text-red-400"
                      : "border-(--border) bg-(--bg-secondary) text-(--text-tertiary)",
                  isCurrent && "ring-2 ring-green-200 dark:ring-green-800",
                )}
              >
                <Icon className="h-4 w-4" />
              </div>
              {!isLast ? (
                <div
                  className={cn(
                    "w-0.5 h-6",
                    isCompleted && stepOrder < currentStep
                      ? "bg-green-300 dark:bg-green-700"
                      : "bg-(--border)",
                  )}
                />
              ) : null}
            </div>

            {/* Label + timestamp */}
            <div className="pt-1 pb-3">
              <div
                className={cn(
                  "text-[13px] font-medium",
                  isCompleted
                    ? "text-(--text-primary)"
                    : "text-(--text-tertiary)",
                )}
              >
                {step.label}
              </div>
              {isCompleted && ts ? (
                <div className="text-[11px] text-(--text-tertiary) tabular-nums">
                  {formatDateTime(ts)}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}

      {/* Failed indicator */}
      {isFailed ? (
        <div className="flex items-start gap-3">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 border-2 border-red-500 bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 ring-2 ring-red-200 dark:ring-red-800">
              <AlertTriangle className="h-4 w-4" />
            </div>
          </div>
          <div className="pt-1 pb-3">
            <div className="text-[13px] font-medium text-red-600 dark:text-red-400">
              Delivery Failed
            </div>
            <div className="text-[11px] text-(--text-tertiary)">
              {delivery.notes || "No failure details provided."}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Create delivery form
 * ───────────────────────────────────────────────────────────── */

type CreateDeliveryValues = {
  invoiceId: string;
  invoiceNumber: string;
  dealerId: string;
  dealerName: string;
  items: Array<{ productName: string; qty: number }>;
  expectedDeliveryAt: string;
  driverName: string;
  driverPhone: string;
  vehicleNumber: string;
  deliveryAddress: string;
  notes: string;
};

/** Stub invoices for the create form invoice picker. */
const STUB_INVOICES = [
  {
    id: "inv_new_1",
    number: "INV-2026-0020",
    dealerId: "dealer_01",
    dealerName: "Dhaka Wholesale Traders",
    address: "45 Motijheel C/A, Dhaka-1000",
    items: [
      { productName: "Basmati Rice 5kg", qty: 60 },
      { productName: "Sunflower Oil 1L", qty: 40 },
    ],
  },
  {
    id: "inv_new_2",
    number: "INV-2026-0021",
    dealerId: "dealer_02",
    dealerName: "Chittagong Supply Co.",
    address: "12 Agrabad C/A, Chittagong-4100",
    items: [
      { productName: "Atta Flour 10kg", qty: 80 },
      { productName: "Sugar 5kg", qty: 50 },
    ],
  },
  {
    id: "inv_new_3",
    number: "INV-2026-0022",
    dealerId: "dealer_05",
    dealerName: "Khulna Distribution Hub",
    address: "56 KDA Avenue, Khulna-9000",
    items: [
      { productName: "Mustard Oil 500ml", qty: 100 },
      { productName: "Turmeric Powder 200g", qty: 120 },
      { productName: "Salt 1kg", qty: 200 },
    ],
  },
  {
    id: "inv_new_4",
    number: "INV-2026-0023",
    dealerId: "dealer_06",
    dealerName: "Barisal Fresh Market",
    address: "34 Sadar Road, Barisal-8200",
    items: [{ productName: "Basmati Rice 5kg", qty: 45 }],
  },
];

function CreateDeliveryForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (values: CreateDeliveryValues) => void;
  onCancel: () => void;
}) {
  const [selectedInvoice, setSelectedInvoice] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [notes, setNotes] = useState("");

  const invoice = STUB_INVOICES.find((i) => i.id === selectedInvoice) ?? null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!invoice) {
      toast.error("Please select an invoice.");
      return;
    }
    if (!driverName.trim()) {
      toast.error("Driver name is required.");
      return;
    }
    if (!vehicleNumber.trim()) {
      toast.error("Vehicle number is required.");
      return;
    }
    if (!expectedDate) {
      toast.error("Expected delivery date is required.");
      return;
    }
    onSubmit({
      invoiceId: invoice.id,
      invoiceNumber: invoice.number,
      dealerId: invoice.dealerId,
      dealerName: invoice.dealerName,
      items: invoice.items,
      expectedDeliveryAt: expectedDate,
      driverName: driverName.trim(),
      driverPhone: driverPhone.trim(),
      vehicleNumber: vehicleNumber.trim(),
      deliveryAddress: invoice.address,
      notes: notes.trim(),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      {/* Invoice selection */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="invoice-select">Invoice</Label>
        <Select
          value={selectedInvoice}
          onValueChange={(v: string) => setSelectedInvoice(v)}
        >
          <SelectTrigger id="invoice-select">
            <SelectValue placeholder="Select an invoice..." />
          </SelectTrigger>
          <SelectContent>
            {STUB_INVOICES.map((inv) => (
              <SelectItem key={inv.id} value={inv.id}>
                {inv.number} - {inv.dealerName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Auto-filled dealer info */}
      {invoice ? (
        <div className="rounded-lg border bg-(--bg-secondary) p-3 flex flex-col gap-2">
          <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
            Auto-filled from invoice
          </div>
          <div className="grid grid-cols-2 gap-2 text-[13px]">
            <div>
              <span className="text-(--text-tertiary)">Dealer: </span>
              <span className="text-(--text-primary) font-medium">
                {invoice.dealerName}
              </span>
            </div>
            <div>
              <span className="text-(--text-tertiary)">Address: </span>
              <span className="text-(--text-primary)">{invoice.address}</span>
            </div>
          </div>
          <div className="text-[11px] text-(--text-tertiary) mt-1">
            Items:
          </div>
          <div className="flex flex-col gap-1">
            {invoice.items.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between text-[13px] text-(--text-secondary)"
              >
                <span>{item.productName}</span>
                <span className="tabular-nums font-mono">
                  x{item.qty}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Driver info */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="driver-name">Driver Name</Label>
          <Input
            id="driver-name"
            placeholder="e.g. Kamal Hossain"
            value={driverName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDriverName(e.target.value)
            }
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="driver-phone">Driver Phone</Label>
          <Input
            id="driver-phone"
            placeholder="+880-1711-234567"
            value={driverPhone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDriverPhone(e.target.value)
            }
          />
        </div>
      </div>

      {/* Vehicle + date */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="vehicle-number">Vehicle Number</Label>
          <Input
            id="vehicle-number"
            placeholder="e.g. DHA-KA-21-4532"
            value={vehicleNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setVehicleNumber(e.target.value)
            }
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="expected-date">Expected Delivery</Label>
          <Input
            id="expected-date"
            type="date"
            value={expectedDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setExpectedDate(e.target.value)
            }
          />
        </div>
      </div>

      {/* Notes */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="delivery-notes">Notes (optional)</Label>
        <Input
          id="delivery-notes"
          placeholder="Special delivery instructions..."
          value={notes}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setNotes(e.target.value)
          }
        />
      </div>

      {/* Submit */}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          <Plus className="h-4 w-4" /> Create Delivery
        </Button>
      </div>
    </form>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail field component
 * ───────────────────────────────────────────────────────────── */

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">
        {value}
      </div>
    </div>
  );
}
